import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../components/NavBar';
import { createChallenge } from '../services/api';
import { ArrowLeft, Trophy, Calendar, Target, Info } from 'lucide-react';

function CreateChallenge() {
  const navigate = useNavigate();
  const location = useLocation();
  const clubId = location.state?.clubId;

  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    isRecurring: false, // ✨ NOUVEAU
    enabledTypes: [],   // ✨ NOUVEAU
    type: 'distance_totale',
    objectifValeur: '',
    dateDebut: '',
    dateFin: '',
    distanceMin: 0,
    allureMin: 10
  });

  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!clubId) {
      alert('Erreur : Club non spécifié');
      return;
    }

    // Validation pour les challenges récurrents
    if (formData.isRecurring && !formData.type) {
      alert('Veuillez sélectionner un type de challenge');
      return;
    }

    // Validation pour les challenges normaux
    if (!formData.isRecurring && (!formData.dateDebut || !formData.dateFin)) {
      alert('Veuillez spécifier les dates de début et de fin');
      return;
    }

    setLoading(true);

    try {
      const challengeData = {
        clubId: clubId,
        nom: formData.nom,
        description: formData.description,
        isRecurring: formData.isRecurring,
        regles: {
          typesActivites: ['Run', 'Trail'],
          distanceMin: parseFloat(formData.distanceMin),
          allureMin: parseFloat(formData.allureMin)
        }
      };

      // Pour un challenge récurrent
      if (formData.isRecurring) {
        // Envoyer le type sélectionné comme un array
        challengeData.enabledTypes = [formData.type];
      } else {
        // Pour un challenge normal
        challengeData.type = formData.type;
        challengeData.objectif = {
          valeur: parseFloat(formData.objectifValeur),
          unite: formData.type === 'distance_totale' ? 'km' : 'courses'
        };
        challengeData.dateDebut = formData.dateDebut;
        challengeData.dateFin = formData.dateFin;
      }

      await createChallenge(challengeData);
      alert('✅ Challenge créé avec succès !');
      navigate(`/club/${clubId}`);
    } catch (error) {
      console.error('Erreur:', error);
      alert(error.response?.data?.message || 'Erreur lors de la création');
      setLoading(false);
    }
  };

  if (!clubId) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-red-900/20 border-2 border-red-500 rounded-xl p-6 text-center">
            <p className="text-red-400 font-semibold">Erreur : Club non spécifié</p>
            <button
              onClick={() => navigate('/')}
              className="mt-4 px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg shadow-lg shadow-red-500/20 transition"
            >
              Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate(`/club/${clubId}`)}
          className="flex items-center text-indigo-500 hover:text-indigo-400 font-semibold mb-6 transition"
        >
          <ArrowLeft size={20} className="mr-2" />
          Retour au club
        </button>

        <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-yellow-900/30 border border-yellow-500 p-3 rounded-xl">
              <Trophy size={32} className="text-yellow-500" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Créer un challenge</h1>
              <p className="text-gray-400">Motivez les membres de votre club !</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Toggle pour challenge récurrent */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-6">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <h2 className="text-xl font-bold text-white">Challenge hebdomadaire récurrent</h2>
                <p className="text-sm text-gray-400 mt-1">
                  Se répète automatiquement chaque semaine du lundi au dimanche
                </p>
              </div>
              <input
                type="checkbox"
                checked={formData.isRecurring}
                onChange={(e) => setFormData({ ...formData, isRecurring: e.target.checked })}
                className="w-6 h-6 accent-indigo-500"
              />
            </label>
          </div>
          {/* Informations de base */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-white mb-4">Informations du challenge</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Nom du challenge *
                </label>
                <input
                  type="text"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 placeholder-gray-500"
                  placeholder="Ex: 100km en Novembre"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 placeholder-gray-500"
                  rows="4"
                  placeholder="Décrivez le challenge..."
                  required
                />
              </div>
            </div>
          </div>

          {/* Type de challenge */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-white mb-2">
              Type de challenge
            </h2>
            {formData.isRecurring && (
              <p className="text-sm text-gray-400 mb-4">
                Pour un challenge récurrent, sélectionnez un seul type qui se répétera chaque semaine
              </p>
            )}

            <div className="space-y-3">
              {['distance_totale', 'nombre_courses', 'denivele_total', 'vitesse_moyenne'].map(typeOption => (
                <label key={typeOption} className={`flex items-start p-4 bg-slate-800 border-2 rounded-lg cursor-pointer hover:border-indigo-500 transition ${
                  formData.type === typeOption ? 'border-indigo-500' : 'border-slate-700'
                }`}>
                  <input
                    type="radio"
                    name="type"
                    value={typeOption}
                    checked={formData.type === typeOption}
                    onChange={(e) => {
                      setFormData({ ...formData, type: e.target.value });
                    }}
                    className="mt-1 accent-indigo-500"
                  />
                  <div className="ml-3">
                    <div className="font-semibold text-white">
                      {typeOption === 'distance_totale' && 'Distance totale'}
                      {typeOption === 'nombre_courses' && 'Nombre de courses'}
                      {typeOption === 'denivele_total' && 'Dénivelé positif'}
                      {typeOption === 'vitesse_moyenne' && 'Vitesse moyenne'}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Objectif - Seulement pour les challenges normaux */}
          {!formData.isRecurring && (
            <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-white mb-4">Objectif</h2>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  {formData.type === 'distance_totale' ? 'Distance cible (km)' : 'Nombre de courses'}
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.objectifValeur}
                  onChange={(e) => setFormData({ ...formData, objectifValeur: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 placeholder-gray-500"
                  placeholder={formData.type === 'distance_totale' ? 'Ex: 100' : 'Ex: 10'}
                  required
                />
              </div>
            </div>
          )}

          {/* Période */}
          {!formData.isRecurring && (
          <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Calendar size={20} className="text-indigo-500" />
              Période du challenge
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Date de début *
                </label>
                <input
                  type="date"
                  value={formData.dateDebut}
                  onChange={(e) => setFormData({ ...formData, dateDebut: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Date de fin *
                </label>
                <input
                  type="date"
                  value={formData.dateFin}
                  onChange={(e) => setFormData({ ...formData, dateFin: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
            </div>
          </div>
          )}
          {/* Règles */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Target size={20} className="text-indigo-500" />
              Règles
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Distance minimum par course (km)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.distanceMin}
                  onChange={(e) => setFormData({ ...formData, distanceMin: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 placeholder-gray-500"
                  placeholder="Ex: 3"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Allure minimum (min/km) - Éviter la marche
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.allureMin}
                  onChange={(e) => setFormData({ ...formData, allureMin: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 placeholder-gray-500"
                  placeholder="Ex: 10"
                />
              </div>

              <div className="p-4 bg-blue-900/20 border border-blue-500 rounded-lg">
                <div className="flex items-start gap-2">
                  <Info size={20} className="text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-300">
                    <p className="font-semibold mb-1">Types d'activités acceptées</p>
                    <p className="text-blue-400">Course à pied (Run) et Trail uniquement</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Boutons */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate(`/club/${clubId}`)}
              className="flex-1 px-6 py-3 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-indigo-500 text-white rounded-lg font-semibold transition"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white rounded-lg font-semibold transition shadow-lg shadow-indigo-500/20 disabled:opacity-50"
            >
              {loading ? 'Création...' : 'Créer le challenge'}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}

export default CreateChallenge;